<template>
  <a-modal
    :title="formTitle || '新增工作流明细'"
    v-model:visible="open"
    @cancel="onClose"
    :width="1100"
    :maskClosable="false"
    :keyboard="false"
    :destroyOnClose="true"
    :footer="null"
    class="create-form-modal"
  >
    <a-form-model ref="form" :model="form" :rules="rules" :label-col="{ flex: '0 0 150px' }" :wrapper-col="{ flex: '1 1 auto' }" class="two-column-form">
      <a-row :gutter="24">
        <a-col :span="12">
          <a-form-model-item label="手环mac" prop="wristbandMac">
            <a-input
              v-model:value="form.wristbandMac"
              disabled
              placeholder="自动填入"
            />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="任务序号" prop="taskNumber">
            <a-input-number
              v-model:value="form.taskNumber"
              :min="1"
              :step="1"
              style="width: 100%"
              placeholder="请输入任务序号"
            />
          </a-form-model-item>
        </a-col>
      </a-row>
      <a-row :gutter="24">
        <a-col :span="12">
          <a-form-model-item label="空间名" prop="spaceId">
            <a-select
              v-model:value="form.spaceId"
              show-search
              placeholder="请选择空间名"
              :filter-option="false"
              @search="handleSpaceSearch"
              @focus="handleSpaceFocus"
              allow-clear
              @change="handleSpaceChange"
              style="width: 100%"
            >
              <template v-slot:notFoundContent>
                <a-spin size="small" v-if="spaceLoading" />
              </template>
              <a-select-option
                v-for="space in spaceOptions"
                :key="space.id"
                :value="space.id"
              >
                {{
                  space.name ||
                  space.spaceName ||
                  space.code ||
                  '空间ID：' + space.id
                }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="服务时间(分钟)" prop="serviceMinutes">
            <a-input-number
              v-model:value="form.serviceMinutes"
              :min="0"
              :step="1"
              style="width: 100%"
              placeholder="请输入服务时间(分钟)"
            />
          </a-form-model-item>
        </a-col>
      </a-row>
      <div class="bottom-control">
        <a-space>
          <a-button @click="cancel" class="cancel-btn"> 取消 </a-button>
          <a-button type="primary" :loading="submitLoading" @click="submitForm" class="save-btn">
            保存
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-modal>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'
/**
 * 手环工作流创建/编辑表单组件
 *
 * 功能说明：
 * 1. 支持新增和编辑手环工作流明细
 * 2. 手环MAC自动填充且禁用
 * 3. 空间选择支持远程搜索，根据当前项目ID动态加载
 * 4. 数字输入字段使用数字输入框，确保数据类型正确
 *
 * @author zbtech
 * @date 2025-01-XX
 */
import {
  getWristbandWorkflow,
  addWristbandWorkflow,
  updateWristbandWorkflow,
} from '@/api/system/wristbandWorkflow'
import { listSpace } from '@/api/system/space'

export default {
  name: 'WorkflowCreateForm',
  props: {},
  components: {},
  data() {
    return {
      // 提交按钮加载状态
      submitLoading: false,
      // 表单标题（添加/修改）
      formTitle: '',
      // 空间选项列表（用于下拉选择）
      spaceOptions: [],
      // 空间列表加载状态
      spaceLoading: false,
      // 表单数据对象
      form: {
        id: null, // 主键ID
        wristbandId: null, // 手环ID
        wristbandMac: null, // 手环MAC（自动填充，禁用）
        taskNumber: null, // 任务序号
        spaceId: null, // 空间ID
        spaceName: null, // 空间名称
        serviceMinutes: null, // 服务分钟
      },
      // 表单类型：1-新增，2-修改
      formType: 1,
      // 抽屉显示/隐藏状态
      open: false,
      // 表单验证规则
      rules: {
        taskNumber: [
          { required: true, message: '任务序号不能为空', trigger: 'blur' },
        ],
        spaceId: [{ required: true, message: '请选择空间', trigger: 'change' }],
      },
    }
  },
  created() {},
  computed: {},
  watch: {},
  mounted() {},
  methods: {
    /**
     * 关闭抽屉
     */
    onClose() {
      this.open = false
    },
    /**
     * 取消按钮操作
     * 关闭抽屉并重置表单
     */
    cancel() {
      this.open = false
      this.reset()
    },
    /**
     * 表单重置
     * 清空所有表单字段和空间选项
     */
    reset() {
      this.formType = 1
      this.form = {
        id: null,
        wristbandId: null,
        wristbandMac: null,
        taskNumber: null,
        spaceId: null,
        spaceName: null,
        serviceMinutes: null,
      }
      this.spaceOptions = []
    },
    /**
     * 新增按钮操作
     * 打开新增表单，自动填充手环ID并加载空间列表
     * @param {Number} wristbandId - 手环ID
     */
    handleAdd(wristbandId) {
      this.reset()
      this.formType = 1
      this.open = true
      this.formTitle = '添加'
      // 如果传入了手环ID，自动填充到手环ID字段
      if (wristbandId) {
        this.form.wristbandId = wristbandId
      }
      // 加载空间列表
      this.fetchSpaceOptions()
    },
    /**
     * 修改按钮操作
     * 根据ID获取工作流详情，填充表单并打开编辑表单
     * @param {Object} row - 行数据对象
     * @param {Number|Array} ids - ID或ID数组（备用参数）
     */
    handleUpdate(row, ids) {
      this.reset()
      this.formType = 2
      const id = row ? row.id : ids
      // 获取工作流详情
      getWristbandWorkflow({ id: id }).then((response) => {
        this.form = response.data
        // 加载空间列表，确保当前选中的空间在选项中
        this.fetchSpaceOptions().then(() => {
          this.ensureCurrentSpaceOption()
        })
        this.open = true
        this.formTitle = '修改'
      })
    },
    /**
     * 提交表单
     * 验证表单后，根据formType决定调用新增或修改接口
     */
    submitForm: function () {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.submitLoading = true
          // 判断是修改还是新增
          if (this.form.id !== undefined && this.form.id !== null) {
            // 修改操作
            updateWristbandWorkflow(this.form)
              .then((response) => {
                this.$message.success('修改成功', 3)
                this.open = false
                $emit(this, 'ok')
              })
              .finally(() => {
                this.submitLoading = false
              })
          } else {
            // 新增操作
            addWristbandWorkflow(this.form)
              .then((response) => {
                this.$message.success('新增成功', 3)
                this.open = false
                $emit(this, 'ok')
              })
              .finally(() => {
                this.submitLoading = false
              })
          }
        } else {
          return false
        }
      })
    },
    /**
     * 远程加载空间列表
     * 根据当前项目ID和搜索关键词加载空间列表
     * @param {String} keyword - 搜索关键词（可选）
     * @returns {Promise} 返回Promise对象
     */
    fetchSpaceOptions(keyword = '') {
      // 获取当前项目ID
      const projectId = this.$store.getters.currentProjectId
      if (!projectId) {
        this.spaceOptions = []
        return Promise.resolve()
      }
      this.spaceLoading = true
      // 构建查询参数
      const params = {
        projectId: projectId,
        name: keyword || undefined,
        spaceName: keyword || undefined,
      }
      // 清除空值参数
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key]
        }
      })
      // 调用接口获取空间列表
      return listSpace(params)
        .then((res) => {
          const data = res.data || res.rows || []
          this.spaceOptions = Array.isArray(data) ? data : []
          // 确保当前选中的空间在选项中
          this.ensureCurrentSpaceOption()
        })
        .finally(() => {
          this.spaceLoading = false
        })
    },
    /**
     * 处理空间搜索
     * 当用户在空间下拉框中输入时触发
     * @param {String} value - 搜索关键词
     */
    handleSpaceSearch(value) {
      this.fetchSpaceOptions(value)
    },
    /**
     * 处理空间下拉框获得焦点
     * 如果选项列表为空，则加载空间列表
     */
    handleSpaceFocus() {
      if (!this.spaceOptions.length) {
        this.fetchSpaceOptions()
      }
    },
    /**
     * 处理空间选择变化
     * 当用户选择空间时，自动填充空间名称
     * @param {Number} value - 选中的空间ID
     */
    handleSpaceChange(value) {
      const target = this.spaceOptions.find((item) => item.id === value)
      this.form.spaceName = target
        ? target.name || target.spaceName || ''
        : null
    },
    /**
     * 确保当前选中的空间在选项中
     * 编辑时，如果当前选中的空间不在选项列表中，则添加到列表中
     */
    ensureCurrentSpaceOption() {
      if (!this.form.spaceId) return
      const exists = this.spaceOptions.some(
        (item) => item.id === this.form.spaceId
      )
      if (!exists) {
        // 如果当前空间不在选项中，添加到选项列表
        this.spaceOptions.push({
          id: this.form.spaceId,
          name: this.form.spaceName || `空间ID：${this.form.spaceId}`,
        })
      }
    },
  },
  emits: ['ok'],
}
</script>

<style scoped>
.create-form-modal :deep(.ant-modal-body) {
  max-height: 75vh;
  overflow-y: auto;
  padding: 20px 24px 20px 24px !important;
}

.bottom-control {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.create-form-modal :deep(.ant-form-item) {
  margin-bottom: 16px;
}

.create-form-modal :deep(.ant-form-item:last-of-type) {
  margin-bottom: 24px;
}

.create-form-modal :deep(.ant-form-item-label) {
  padding-bottom: 6px;
  flex: 0 0 150px !important;
  max-width: 150px !important;
}

/* 两列布局样式 */
.two-column-form :deep(.ant-row) {
  margin-bottom: 0;
}

.two-column-form :deep(.ant-col) {
  margin-bottom: 16px;
}

.two-column-form :deep(.ant-form-item) {
  margin-bottom: 0;
}


/* 保存按钮样式 */
.save-btn {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
  border-radius: 4px !important;
}

.save-btn:hover {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
  color: #FFFFFF !important;
  opacity: 1;
}

.save-btn:focus {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
}

/* 取消按钮样式 */
.cancel-btn {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
  border-radius: 4px !important;
}

.cancel-btn:hover {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
}

.cancel-btn:focus {
  background-color: #F2F2F2 !important;
  border-color: #F2F2F2 !important;
  color: #333333 !important;
}
</style>

<style>
/* 输入框样式 - 使用非 scoped 样式确保生效 */
.create-form-modal .ant-input,
.create-form-modal .ant-input:hover,
.create-form-modal .ant-input:focus,
.create-form-modal .ant-input:disabled {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal .ant-input-affix-wrapper,
.create-form-modal .ant-input-affix-wrapper:hover,
.create-form-modal .ant-input-affix-wrapper-focused {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 带前缀/后缀的输入框内部input去掉边框，避免嵌套 */
.create-form-modal .ant-input-affix-wrapper .ant-input {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* 选择框样式 */
.create-form-modal .ant-select-selector,
.create-form-modal .ant-select-selector:hover,
.create-form-modal .ant-select-focused .ant-select-selector {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 数字输入框样式 */
.create-form-modal .ant-input-number,
.create-form-modal .ant-input-number:hover,
.create-form-modal .ant-input-number-focused,
.create-form-modal .ant-input-number:disabled {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal .ant-input-number-input,
.create-form-modal .ant-input-number-input-wrap {
  border-radius: 4px 4px 4px 4px !important;
}
</style>
